<template>
  <component
    :is="baseLink ? BaseLinkComponent : tag"
    v-bind="{
      ...(baseLink && { link: baseLink, placeholder }),
      ...(to && { to })
    }"
    :disabled="disabled"
    :class="[
      'button',
      `button--${color}`,
      `size-${size}`,
      {
        'button--disabled': disabled
      }
    ]"
  >
    <span
      v-if="icon && ['tertiary', 'function', 'function-black'].includes(color)"
      class="content"
    >
      <span class="icon-container">
        <BaseIcon :icon="icon" class="icon" />
      </span>
      <slot />
      <span
        v-if="color === 'tertiary' && (text || baseLink?.label)"
        class="text trim-text"
        v-html="nbsp(text || baseLink?.label)"
      />
    </span>
    <slot v-else>
      <span class="text trim-text" v-html="nbsp(text || baseLink?.label)" />
    </slot>
  </component>
</template>

<script setup lang="ts">
import type { RouteLocationRaw } from 'vue-router'

import { NuxtLink } from '#components'
import type { PublicLink } from '@/service/__generated-api'

const BaseLinkComponent = resolveComponent('BaseLink')

type ButtonSize = 'l' | 'm'
type ButtonColorEnum =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'outline-white'
  | 'outline-black'
  | 'primary-grey'
  | 'function'
  | 'function-black'

withDefaults(
  defineProps<{
    color?: ButtonColorEnum
    size?: ButtonSize
    tag?: 'button' | 'span' | 'a' | typeof NuxtLink
    to?: RouteLocationRaw
    text?: string
    icon?: string
    baseLink?: PublicLink | null
    disabled?: boolean
    placeholder?: boolean
  }>(),
  {
    color: 'primary',
    size: 'm',
    tag: 'button',
    to: undefined,
    text: '',
    icon: undefined,
    baseLink: undefined,
    disabled: false,
    placeholder: false
  }
)
</script>

<style lang="scss" scoped>
@mixin apply-colors($color, $bg, $border: $bg) {
  color: $color;
  background-color: $bg;
  border-color: $border;
}

@mixin hover {
  &:not(&--disabled):hover,
  &:not(:disabled):hover {
    @content;
  }
}

@mixin focus-visible {
  &:not(&--disabled):focus-visible,
  &:not(:disabled):focus-visible {
    @content;
  }
}

@mixin disabled {
  &--disabled,
  &:disabled {
    @content;
  }
}

.button {
  $outline-width: 2px;

  @include center-content;
  display: inline-flex;
  align-items: center;
  width: fit-content;
  max-width: 100%;
  outline: $outline-width solid transparent;
  outline-offset: 0;
  transition:
    color $transition,
    background-color $transition,
    border-color $transition,
    outline-color $transition;

  @include disabled {
    pointer-events: none;
    user-select: none;
  }

  &:not(:disabled),
  &:not(&--disabled) {
    cursor: pointer;
  }

  .icon :deep(svg) {
    display: block;
  }

  &--primary,
  &--primary-grey,
  &--outline-white,
  &--outline-black {
    font-weight: 600;
    line-height: 1.5;
    border: 2px solid transparent;
    border-radius: 40px;

    &.size-m {
      padding: rem(6.75px) rem(12px);
      font-size: rem(13px);
    }

    &.size-l {
      padding: rem(12.5px) rem(22px);
      font-size: rem(14px);
    }
  }

  &--primary,
  &--outline-white,
  &--outline-black,
  &--secondary,
  &--tertiary {
    @include focus-visible {
      outline-color: $global-sun-04;
    }
  }

  &--primary,
  &--primary-grey {
    @include disabled {
      @include apply-colors($global-grey-04, $global-grey-05);
    }
  }

  &--outline-white,
  &--outline-black {
    border-color: $global-sun-01;

    @include hover {
      border-color: $global-sun-02;
    }

    @include disabled {
      color: $global-grey-04;
      border-color: $global-grey-05;
    }
  }

  &--primary {
    @include apply-colors($global-white, $global-sun-01);

    @include hover {
      background-color: $global-sun-02;
      border-color: $global-sun-02;
    }
  }

  &--primary-grey {
    @include apply-colors($global-black, $global-grey-05);

    @include hover {
      background-color: $global-grey-06;
      border-color: $global-grey-06;
    }

    @include focus-visible {
      outline-color: $global-grey-04;
    }
  }

  &--outline-white {
    color: $global-white;
  }

  &--outline-black {
    color: $global-black;
  }

  &--secondary {
    position: relative;
    font-size: rem(14px);
    font-weight: 600;
    line-height: 1.65;

    @include hover {
      &::after {
        background-color: $global-sun-02;
      }
    }

    @include disabled {
      color: $global-grey-04;

      &::after {
        background-color: $global-grey-04;
      }
    }

    &::after {
      $underline-height: 2px;

      position: absolute;
      inset: auto 0 calc(($underline-height + $outline-width) * -1);
      height: $underline-height;
      content: '';
      background-color: $global-sun-01;
    }
  }

  &--tertiary {
    max-width: 100%;
    font-size: rem(14px);
    font-weight: 600;
    line-height: 1.65;

    @include hover {
      .icon-container {
        background-color: $global-sun-02;
      }
    }

    @include disabled {
      color: $global-grey-04;

      .icon-container {
        background-color: $global-grey-04;
      }
    }

    .content {
      display: flex;
      gap: rem(16px);
      align-items: center;
      max-width: 100%;
    }

    .text {
      inline-size: calc(100% - 44px - #{rem(16px)});
      overflow-wrap: break-word;
    }

    .icon-container {
      @include size(44px);
      @include center-content();
      flex-shrink: 0;

      color: $global-white;
      background-color: $global-sun-01;
      border-radius: 50%;
      transition:
        color $transition,
        background-color $transition;
    }
  }

  &--function {
    @include hover {
      .icon-container {
        color: $global-sun-02;
        border-color: $global-sun-02;
      }
    }

    .icon-container {
      @include size(53px);
      @include center-content();
      color: $global-sun-01;
      border: 1px solid $global-sun-01;
      border-radius: 50%;

      transition:
        color $transition,
        border-color $transition;
    }
  }

  &--function-black {
    @include hover {
      .icon-container {
        background-color: $global-grey-02;
      }
    }

    .icon-container {
      @include size(24px);
      @include center-content();
      color: $global-white;
      background-color: $global-grey-01;
      border-radius: 50%;

      transition:
        background-color $transition,
        color $transition;
    }
  }
}
</style>
